import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, BookOpen, Network, Search } from 'lucide-react';
import { listContentItems } from '../services/api';
import type { ContentItem, PipelineStatus } from '../types/v2';

const STATUS_COLORS: Record<string, string> = {
  INGESTED:           'bg-gray-700 text-gray-300',
  CHUNKING:           'bg-blue-900 text-blue-300',
  CHUNKED:            'bg-blue-900 text-blue-300',
  EMBEDDING:          'bg-blue-900 text-blue-200',
  EMBEDDED:           'bg-blue-900 text-blue-200',
  ANALYZING:          'bg-orange-900 text-orange-300',
  CONCEPT_EXTRACTION: 'bg-orange-900 text-orange-300',
  RELATION_EXTRACTION:'bg-orange-900 text-orange-300',
  ANALYZED:           'bg-yellow-900 text-yellow-300',
  DEDUPLICATION:      'bg-yellow-900 text-yellow-300',
  VALIDATION:         'bg-yellow-900 text-yellow-300',
  VALIDATED:          'bg-yellow-900 text-yellow-200',
  GRAPH_SYNCING:      'bg-green-900 text-green-300',
  GRAPH_SYNCED:       'bg-green-800 text-green-200',
  ERROR:              'bg-red-900 text-red-300',
};

const SOURCE_LABELS: Record<string, string> = {
  upload_document: 'Documento',
  news_api:        'News',
  web_scraping:    'Web',
  rss_feed:        'RSS',
  social_api:      'Social',
};

export function LibraryPage() {
  const navigate = useNavigate();
  const [items, setItems]   = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter]     = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listContentItems();
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) => {
    if (search && !((i.title ?? '').toLowerCase().includes(search.toLowerCase()))) return false;
    if (statusFilter && i.pipeline_status !== statusFilter) return false;
    if (typeFilter && i.source_type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold">Knowledge Base</h2>
          <span className="text-gray-500 text-sm">({items.length} fonti)</span>
        </div>
        <button onClick={load} className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" />
          Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-500" />
          <input
            type="text"
            placeholder="Cerca titolo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 w-56"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">Tutti i tipi</option>
          {Object.entries(SOURCE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">Tutti gli status</option>
          <option value="GRAPH_SYNCED">Nel grafo</option>
          <option value="ERROR">Errore</option>
          <option value="INGESTED">Ingerito</option>
          <option value="VALIDATED">Validato</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Caricamento...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-sm">
          {items.length === 0 ? 'Nessuna fonte nel knowledge base. Carica un documento o approva elementi dall\'Inbox.' : 'Nessun risultato per i filtri selezionati.'}
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-medium">Titolo</th>
                <th className="text-left px-4 py-3 font-medium">Tipo</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Entità</th>
                <th className="text-right px-4 py-3 font-medium">Relazioni</th>
                <th className="text-left px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map((item) => (
                <tr key={item.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-gray-200 max-w-xs truncate">
                    {item.title || <span className="text-gray-600 italic">senza titolo</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-400">{SOURCE_LABELS[item.source_type] ?? item.source_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.pipeline_status] ?? 'bg-gray-700 text-gray-300'}`}>
                      {item.pipeline_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400">{item.entity_count}</td>
                  <td className="px-4 py-3 text-right text-gray-400">{item.validated_relation_count}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {new Date(item.ingested_at).toLocaleDateString('it-IT')}
                  </td>
                  <td className="px-4 py-3">
                    {item.pipeline_status === 'GRAPH_SYNCED' && (
                      <button
                        onClick={() => navigate(`/graph?focus=${encodeURIComponent(item.title ?? '')}`)}
                        className="p-1.5 text-gray-500 hover:text-blue-400 transition-colors"
                        title="Visualizza nel grafo"
                      >
                        <Network className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
